import React, { useState } from "react";
import { ChevronDown, Mail, Check, ArrowRight } from "lucide-react";
import { submitContactForm } from "../utils/api";
import {
  Button,
  Container,
  Section,
  Eyebrow,
  Display,
  Hairline,
  SEO,
} from "./ui";
import { canonicalFor } from "../routes";

const FAQ_CATEGORIES = [
  {
    label: "Getting started",
    items: [
      {
        q: "What is an NFC business card?",
        a: "An NFC business card is a thin PVC card with a small chip embedded inside. Hold it within an inch of any modern phone and your WhoBrew page opens in the recipient's browser. There is no app to install on either side. Every card also has a printed QR code on the back as a fallback for older phones.",
      },
      {
        q: "How do I update my card?",
        a: "Go to My Card and edit. Changes are live the second you save. The same URL keeps pointing to your card forever, so previously shared cards update automatically.",
      },
    ],
  },
  {
    label: "Cards",
    items: [
      {
        q: "Do NFC cards work with all phones?",
        a: "NFC works with iPhone 7 and newer (iOS 11+) and every Android from the last eight years. Each card has a printed QR code on the back as a backup for older phones. The QR fallback covers less than two percent of phones in circulation.",
      },
      {
        q: "How many links can I add to my card?",
        a: "Free accounts get up to five links. Pro accounts get up to ten links per card and three cards per account.",
      },
      {
        q: "Can I have a different card for different roles?",
        a: "Yes. Pro accounts include up to three separate cards, each with its own URL, theme, and link list. Common pattern: one personal card, one work card, one project card.",
      },
    ],
  },
  {
    label: "Billing",
    items: [
      {
        q: "What is the return policy?",
        a: "30-day money-back guarantee on physical cards and Pro subscriptions. Email support@whobrew.us and we'll process the refund the same day.",
      },
      {
        q: "Can I cancel my Pro subscription anytime?",
        a: "Yes. Cancel from your account settings. You keep Pro access until the end of the current billing period. No fees, no questions.",
      },
      {
        q: "Do you offer team plans?",
        a: "Yes. WhoBrew for Teams is $3.99 per person per month with centralized branding, admin controls, bulk card ordering, and aggregated analytics. See /teams for details.",
      },
    ],
  },
];

const ALL_FAQ = FAQ_CATEGORIES.flatMap((c) => c.items);

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: ALL_FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Help() {
  const [openFAQ, setOpenFAQ] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitContactForm({ name, email, subject: "Support request", message });
    } catch {}
    setSending(false);
    setSubmitted(true);
    setName("");
    setEmail("");
    setMessage("");
  };

  return (
    <>
      <SEO
        title="Help — WhoBrew"
        description="Help center for WhoBrew. Common questions, setup guides, and a direct line to support."
        canonical={canonicalFor("/help")}
        jsonLd={FAQ_JSONLD}
      />

      {/* Hero */}
      <Section tone="page" spacing="loose" hairlineBottom>
        <Container width="prose">
          <Eyebrow>Help</Eyebrow>
          <Display as="h1" scale="lg" italicWord="help" className="mt-5">
            How can we help?
          </Display>
          <p className="mt-7 text-lg text-coffee-700 dark:text-cream-200 dark:text-cream-200 leading-relaxed">
            Browse the common questions or send us a message. A real person on our team replies
            within one business day.
          </p>
        </Container>
      </Section>

      {/* FAQ by category */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container width="prose">
          <div className="space-y-16">
            {FAQ_CATEGORIES.map((category) => (
              <div key={category.label}>
                <Eyebrow>{category.label}</Eyebrow>
                <Display as="h2" scale="md" className="mt-4 mb-8">
                  {category.label}.
                </Display>
                <div className="divide-y divide-latte-300 dark:divide-latte-800 border-y border-latte-300 dark:border-latte-800 dark:border-latte-800">
                  {category.items.map((item) => {
                    const id = `${category.label}-${item.q}`;
                    const open = openFAQ === id;
                    return (
                      <div key={item.q}>
                        <button
                          onClick={() => setOpenFAQ(open ? null : id)}
                          aria-expanded={open}
                          className="w-full text-left py-6 flex items-start justify-between gap-6"
                        >
                          <span className="font-display text-headline text-coffee-900 dark:text-cream-50 dark:text-cream-50 leading-snug">
                            {item.q}
                          </span>
                          <ChevronDown
                            className={`w-5 h-5 mt-2 text-coffee-700 dark:text-cream-200 dark:text-cream-200 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
                          />
                        </button>
                        {open && (
                          <div className="pb-8 text-body text-coffee-700 dark:text-cream-200 dark:text-cream-200 leading-relaxed">
                            {item.a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      <Hairline />

      {/* Direct contact */}
      <Section tone="raised" spacing="default" hairlineBottom>
        <Container width="prose">
          <Eyebrow>Still stuck</Eyebrow>
          <Display as="h2" scale="md" italicWord="real" className="mt-4 mb-6">
            A real person reads every message.
          </Display>
          <p className="text-body text-coffee-700 dark:text-cream-200 dark:text-cream-200 leading-relaxed mb-8">
            Email support@whobrew.us or send a message below. We reply within one business day,
            usually faster.
          </p>
          <div className="flex gap-3 mb-10">
            <Button href="mailto:support@whobrew.us" leadingIcon={<Mail className="w-4 h-4" />}>
              Email support
            </Button>
          </div>

          {submitted ? (
            <div className="border border-latte-300 dark:border-latte-800 dark:border-latte-800 rounded-lg p-10 bg-cream-50 dark:bg-coffee-900 dark:bg-coffee-900">
              <Check className="w-8 h-8 text-success-700 mb-4" strokeWidth={2.5} />
              <Display as="h3" scale="sm">Sent.</Display>
              <p className="mt-4 text-body text-coffee-700 dark:text-cream-200 dark:text-cream-200">
                A real person on our team will reply within one business day.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <FormField id="name" label="Name">
                <input id="name" type="text" required value={name} onChange={(e) => setName(e.target.value)} className="help-input" placeholder="Your name" />
              </FormField>
              <FormField id="email" label="Email">
                <input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="help-input" placeholder="you@example.com" />
              </FormField>
              <FormField id="message" label="Message">
                <textarea id="message" required rows={5} value={message} onChange={(e) => setMessage(e.target.value)} className="help-input resize-vertical" placeholder="What's going on?" />
              </FormField>
              <Button type="submit" size="lg" loading={sending}>
                {sending ? "Sending" : "Send message"}
              </Button>
              <style>{`
                .help-input {
                  width: 100%;
                  padding: 0.75rem 1rem;
                  background: transparent;
                  border: 1px solid #c9b89e;
                  border-radius: 4px;
                  color: inherit;
                  font-family: inherit;
                  font-size: 1rem;
                  outline: none;
                  transition: border-color 150ms;
                }
                .help-input:focus { border-color: #1f1410; }
                .dark .help-input { border-color: #443a30; }
                .dark .help-input:focus { border-color: #fbf7f1; }
              `}</style>
            </form>
          )}
        </Container>
      </Section>

      {/* CTA */}
      <Section tone="ink" spacing="default">
        <Container className="text-center">
          <Display as="h2" scale="lg" italicWord="card" className="mx-auto max-w-3xl">
            Make a card you actually want to hand out.
          </Display>
          <div className="mt-10 flex justify-center">
            <Button to="/my-card" size="lg" onInk trailingIcon={<ArrowRight className="w-4 h-4" />}>
              Open my card
            </Button>
          </div>
        </Container>
      </Section>
    </>
  );
}

function FormField({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="block font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200 dark:text-cream-200 mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}
